import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { workRpc } from './api';
import { compressImage } from './images';
import { workKeys } from './hooks';
import type { WorkstationPart, WorkstationPhoto, WorkstationTool } from './types';

/**
 * Workstation setup — the photos, tools and parts that describe a bench
 * (migration 144), plus the items built there. Reads hit the tables (RLS:
 * org-wide read); writes are direct for the three child tables (manager
 * policy) and go through `work_station_set_items` for the item link.
 */

export const stationSetupKeys = {
  photos: (stationId: string | null) => ['work', 'station', stationId, 'photos'] as const,
  tools: (stationId: string | null) => ['work', 'station', stationId, 'tools'] as const,
  parts: (stationId: string | null) => ['work', 'station', stationId, 'parts'] as const,
  items: (stationId: string | null) => ['work', 'station', stationId, 'items'] as const,
};

function safeFilename(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'photo.jpg';
}

/** Compress + upload a station photo. Returns the object key. */
export async function uploadStationImage(
  orgId: string,
  stationId: string,
  file: File,
): Promise<string> {
  const blob = await compressImage(file, 1600, 0.85);
  const upload = new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
    type: 'image/jpeg',
  });
  const path = `${orgId}/stations/${stationId}/${Date.now()}-${safeFilename(upload.name)}`;
  const { error } = await supabase.storage.from('work-instructions').upload(path, upload, {
    upsert: false,
    cacheControl: '3600',
    contentType: 'image/jpeg',
  });
  if (error) throw error;
  return path;
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

export function useStationPhotos(stationId: string | null) {
  return useQuery({
    queryKey: stationSetupKeys.photos(stationId),
    enabled: !!stationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstation_photos' as never)
        .select('*')
        .eq('workstation_id', stationId!)
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as unknown as WorkstationPhoto[];
    },
  });
}

export function useAddStationPhoto() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      stationId: string;
      file: File;
      caption?: string | null;
      position: number;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = await uploadStationImage(profile.organization_id, input.stationId, input.file);
      const { data, error } = await supabase
        .from('workstation_photos' as never)
        .insert({
          organization_id: profile.organization_id,
          workstation_id: input.stationId,
          image_path: path,
          caption: input.caption?.trim() || null,
          position: input.position,
          created_by: user?.id ?? null,
        } as never)
        .select('*')
        .single();
      if (error) {
        void supabase.storage.from('work-instructions').remove([path]);
        throw error;
      }
      return data as unknown as WorkstationPhoto;
    },
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.photos(vars.stationId) });
    },
  });
}

export function useUpdateStationPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      stationId: string;
      caption?: string | null;
      position?: number;
    }) => {
      const patch: Record<string, unknown> = {};
      if (input.caption !== undefined) patch.caption = input.caption?.trim() || null;
      if (input.position !== undefined) patch.position = input.position;
      const { data, error } = await supabase
        .from('workstation_photos' as never)
        .update(patch as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as WorkstationPhoto;
    },
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.photos(vars.stationId) });
    },
  });
}

export function useDeleteStationPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      stationId: string;
      imagePath: string;
      isCover: boolean;
    }) => {
      const { error } = await supabase
        .from('workstation_photos' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
      if (input.isCover) {
        await workRpc('work_station_configure', {
          p_workstation_id: input.stationId,
          p_clear_cover: true,
        });
      }
      await supabase.storage.from('work-instructions').remove([input.imagePath]);
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.photos(vars.stationId) });
      if (vars.isCover) void qc.invalidateQueries({ queryKey: workKeys.stations });
    },
  });
}

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

export function useStationTools(stationId: string | null) {
  return useQuery({
    queryKey: stationSetupKeys.tools(stationId),
    enabled: !!stationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstation_tools' as never)
        .select('*')
        .eq('workstation_id', stationId!)
        .order('position', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data as unknown as WorkstationTool[];
    },
  });
}

export function useSaveStationTool() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      stationId: string;
      name: string;
      equipmentId: string | null;
      quantity: number;
      notes?: string | null;
      isRequired: boolean;
      position?: number;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Give the tool a name.');
      const payload = {
        organization_id: profile.organization_id,
        workstation_id: input.stationId,
        name,
        equipment_id: input.equipmentId,
        quantity: Math.max(1, Math.round(input.quantity) || 1),
        notes: input.notes?.trim() || null,
        is_required: input.isRequired,
        ...(input.position !== undefined ? { position: input.position } : {}),
      };
      const q = input.id
        ? supabase
            .from('workstation_tools' as never)
            .update(payload as never)
            .eq('id', input.id)
        : supabase.from('workstation_tools' as never).insert(payload as never);
      const { data, error } = await q.select('*').single();
      if (error) throw error;
      return data as unknown as WorkstationTool;
    },
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.tools(vars.stationId) });
    },
  });
}

export function useDeleteStationTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; stationId: string }) => {
      const { error } = await supabase
        .from('workstation_tools' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.tools(vars.stationId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

export function useStationParts(stationId: string | null) {
  return useQuery({
    queryKey: stationSetupKeys.parts(stationId),
    enabled: !!stationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstation_parts' as never)
        .select('*')
        .eq('workstation_id', stationId!)
        .order('position', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return ((data as unknown as WorkstationPart[]) ?? []).map((p) => ({
        ...p,
        quantity: p.quantity === null ? null : Number(p.quantity),
      }));
    },
  });
}

export function useSaveStationPart() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      stationId: string;
      itemId: string | null;
      name: string;
      quantity: number | null;
      uom?: string | null;
      binLocation?: string | null;
      notes?: string | null;
      position?: number;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Give the part a name.');
      const payload = {
        organization_id: profile.organization_id,
        workstation_id: input.stationId,
        item_id: input.itemId,
        name,
        quantity: input.quantity,
        uom: input.uom?.trim() || null,
        bin_location: input.binLocation?.trim() || null,
        notes: input.notes?.trim() || null,
        ...(input.position !== undefined ? { position: input.position } : {}),
      };
      const q = input.id
        ? supabase
            .from('workstation_parts' as never)
            .update(payload as never)
            .eq('id', input.id)
        : supabase.from('workstation_parts' as never).insert(payload as never);
      const { data, error } = await q.select('*').single();
      if (error) throw error;
      return data as unknown as WorkstationPart;
    },
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.parts(vars.stationId) });
    },
  });
}

export function useDeleteStationPart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; stationId: string }) => {
      const { error } = await supabase
        .from('workstation_parts' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.parts(vars.stationId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Items built at the station (item_workstations, station side)
// ---------------------------------------------------------------------------

/** Ids of the items linked to a station through `item_workstations`. */
export function useStationItemIds(stationId: string | null) {
  return useQuery({
    queryKey: stationSetupKeys.items(stationId),
    enabled: !!stationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_workstations' as never)
        .select('item_id')
        .eq('workstation_id', stationId!);
      if (error) throw error;
      return ((data as unknown as Array<{ item_id: string }>) ?? []).map((r) => r.item_id);
    },
  });
}

export function useSetStationItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { stationId: string; itemIds: string[] }) =>
      workRpc<string[]>('work_station_set_items', {
        p_workstation_id: input.stationId,
        p_item_ids: input.itemIds,
      }),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: stationSetupKeys.items(vars.stationId) });
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['item-workstations-for-item'] });
      void qc.invalidateQueries({
        queryKey: ['work', 'signoffs', 'station-instructions', vars.stationId],
      });
      void qc.invalidateQueries({ queryKey: ['work', 'signoffs', 'hashes'] });
    },
  });
}
